import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiFetch, ApiError } from "../lib/apiClient";
import { PrimaryEvidenceFile } from "../lib/types";

type Props = { mode: "create" | "edit" };

export default function PrimaryEvidenceFormPage({ mode }: Props) {
  const { pin = "", id = "" } = useParams();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [acquiredOn, setAcquiredOn] = useState("");
  const [expiresOn, setExpiresOn] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [attachMode, setAttachMode] = useState<"none" | "url" | "file">(mode === "create" ? "url" : "none");

  const [registrantPin, setRegistrantPin] = useState<string>(pin);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (mode !== "edit" || !id) return;
    apiFetch<PrimaryEvidenceFile>(`/api/v1/primary_evidence/${id}`)
      .then(r => {
        if (r.name) setName(r.name);
        if (r.acquired_on) setAcquiredOn(r.acquired_on);
        if (r.expires_on) setExpiresOn(r.expires_on);
        if (r.registrant_pin) setRegistrantPin(r.registrant_pin);
        if (r.files && r.files.length > 0) {
          setFileUrl(r.files[0]);
        }
      })
      .catch(e => {
        if (e instanceof ApiError) setError(`${e.code}: ${e.message}`);
        else setError(e instanceof Error ? e.message : "Failed to load primary evidence");
      });
  }, [mode, id]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setErrors({});

    try {
      if (mode === "create") {
        if (attachMode === "file" && file) {
          const form = new FormData();
          if (name) form.set("name", name);
          if (acquiredOn) form.set("acquired_on", acquiredOn);
          if (expiresOn) form.set("expires_on", expiresOn);
          form.set("file", file);
          await apiFetch(`/api/v1/registrants/${encodeURIComponent(pin)}/primary_evidence`, {
            method: "POST", multipart: form,
          });
        } else {
          const body: Record<string, unknown> = {};
          if (name) body.name = name;
          if (acquiredOn) body.acquired_on = acquiredOn;
          if (expiresOn) body.expires_on = expiresOn;
          if (attachMode === "url" && fileUrl) body.file_url = fileUrl;
          await apiFetch(`/api/v1/registrants/${encodeURIComponent(pin)}/primary_evidence`, {
            method: "POST", body,
          });
        }
        navigate(`/registrants/${pin}`);
      } else {
        if (attachMode === "file" && file) {
          const form = new FormData();
          if (name) form.set("name", name);
          if (acquiredOn) form.set("acquired_on", acquiredOn);
          if (expiresOn) form.set("expires_on", expiresOn);
          form.set("file", file);
          await apiFetch(`/api/v1/primary_evidence/${id}`, { method: "PUT", multipart: form });
        } else {
          const body: Record<string, unknown> = {};
          if (name) body.name = name;
          if (acquiredOn) body.acquired_on = acquiredOn;
          if (expiresOn) body.expires_on = expiresOn;
          if (attachMode === "url" && fileUrl) body.file_url = fileUrl;
          await apiFetch(`/api/v1/primary_evidence/${id}`, { method: "PUT", body });
        }
        navigate(registrantPin ? `/registrants/${registrantPin}` : "/registrants");
      }
    } catch (e) {
      if (e instanceof ApiError) {
        setError(`${e.code}: ${e.message}`);
        setErrors(e.errors ?? {});
      } else {
        setError(e instanceof Error ? e.message : "Request failed");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const backTo = mode === "create" ? `/registrants/${pin}` : (registrantPin ? `/registrants/${registrantPin}` : "/registrants");

  return (
    <div className="max-w-xl space-y-4">
      <div>
        <Link to={backTo} className="text-sm text-brand-700 hover:underline">← Back</Link>
        <h1 className="text-xl font-semibold mt-1">
          {mode === "create"
            ? `Push primary evidence for ${pin}`
            : `Edit primary evidence #${id}`}
        </h1>
        <p className="text-xs text-slate-500 mt-1">
          Primary Evidence covers a registrant's primary clinical certificate.
          Files only — no recertification trees or linked files via the API.
        </p>
      </div>

      {error && (
        <div className="text-red-700 bg-red-50 border border-red-200 rounded p-3 text-sm">
          {error}
          {Object.keys(errors).length > 0 && (
            <ul className="mt-1 list-disc list-inside">
              {Object.entries(errors).map(([k, v]) => <li key={k}><b>{k}:</b> {v}</li>)}
            </ul>
          )}
        </div>
      )}

      <form onSubmit={submit} className="space-y-3 bg-white border border-slate-200 rounded p-4">
        <Field label="Display name (optional)">
          <input type="text" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Paramedic Certificate" />
        </Field>

        <Field label="Acquired on (optional)" error={errors.acquired_on}>
          <input type="date" className="border border-slate-300 rounded px-2 py-1 text-sm" value={acquiredOn} onChange={e => setAcquiredOn(e.target.value)} />
        </Field>

        <Field label="Expires on (optional)" error={errors.expires_on}>
          <input type="date" className="border border-slate-300 rounded px-2 py-1 text-sm" value={expiresOn} onChange={e => setExpiresOn(e.target.value)} />
        </Field>

        <Field label={mode === "create" ? "Evidence file (required)" : "Replace file (optional)"}>
          <div className="space-y-2">
            <div className="flex gap-4 text-sm">
              <label><input type="radio" name="attach" checked={attachMode === "none"} onChange={() => setAttachMode("none")} /> None</label>
              <label><input type="radio" name="attach" checked={attachMode === "url"} onChange={() => setAttachMode("url")} /> file_url</label>
              <label><input type="radio" name="attach" checked={attachMode === "file"} onChange={() => setAttachMode("file")} /> Upload (multipart)</label>
            </div>
            {attachMode === "url" && (
              <input type="url" className="w-full border border-slate-300 rounded px-2 py-1 text-sm mono" placeholder="https://org.example.com/cert.pdf" value={fileUrl} onChange={e => setFileUrl(e.target.value)} />
            )}
            {attachMode === "file" && (
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.docx" onChange={e => setFile(e.target.files?.[0] ?? null)} />
            )}
            {errors.file && <div className="text-red-600 text-xs">{errors.file}</div>}
            {errors.file_url && <div className="text-red-600 text-xs">{errors.file_url}</div>}
          </div>
        </Field>

        <div className="flex gap-2 pt-2">
          <button type="submit" className="bg-brand-600 text-white rounded px-3 py-2 text-sm" disabled={submitting}>
            {submitting ? "Submitting…" : mode === "create" ? "Push primary evidence" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children, error }: { label: string; children: React.ReactNode; error?: string }) {
  return (
    <label className="block">
      <div className="text-xs text-slate-600 mb-1">{label}</div>
      {children}
      {error && <div className="text-red-600 text-xs mt-1">{error}</div>}
    </label>
  );
}
